/**
 * Prescription drug / medicine shared types and helpers.
 */
/**
 * Pattern for one dose segment of a free-text frequency: one or two digits
 * with an optional decimal (max 2 places), e.g. "1", "1.5", "0", "0.25".
 */
const FREQ_SEGMENT = /^\d{1,2}(\.\d{1,2})?$/;
/**
 * Validate a full free-text frequency value: hyphen-separated dose segments
 * ("1-1-1", "1.5-0-1", "1-1-1-1-1-1-1"). Empty string is valid (frequency can
 * be blank when instructions carry the meaning).
 */
export function isValidFrequency(value) {
    if (value === "")
        return true;
    return value.split("-").every((seg) => FREQ_SEGMENT.test(seg));
}
/**
 * Normalize a frequency string: trim, strip whitespace, collapse repeated
 * hyphens, and remove a trailing hyphen or trailing dot (left over from the
 * smart input's live auto-formatting). Returns "" for blank input; otherwise
 * the value is kept verbatim (no numeric reformatting).
 */
export function normalizeFrequency(value) {
    return value
        .trim()
        .replace(/\s+/g, "")
        .replace(/-{2,}/g, "-")
        .replace(/-+$/, "")
        .replace(/\.$/, "");
}
/**
 * Auto-format a frequency string as the user types, inserting hyphens
 * between dose segments live. The grammar of one segment:
 *   - a single digit (0-9) on its own, OR
 *   - a digit followed by a dot and ONE or TWO decimal digits (e.g. "1.5",
 *     "0.5", "0.25", "0.75").
 * The next digit after a complete whole-number segment starts a NEW segment
 * (so "11" -> "1-1"). The next digit after a complete decimal segment
 * (1 or 2 decimal places) also starts a new segment (so "1.51" -> "1.5-1",
 * "0.251" -> "0.25-1"). A second dot ends the current decimal segment and
 * the next digit starts a new one (so "1.5.5" -> "1.5-5").
 *
 * Examples (typing left-to-right):
 *   "1"            -> "1"
 *   "11"           -> "1-1"
 *   "111"          -> "1-1-1"
 *   "1111"         -> "1-1-1-1"
 *   "1.5"          -> "1.5"
 *   "1.51"         -> "1.5-1"
 *   "1.51.5"       -> "1.5-1.5"
 *   "1.51.51.51.5" -> "1.5-1.5-1.5-1.5"
 *   "0.51"         -> "0.5-1"   (3rd digit after dot starts a new segment)
 *   "0.25"         -> "0.25"
 *   "0.251"        -> "0.25-1" (3rd decimal digit starts a new segment)
 *   "0.75"         -> "0.75"
 *   "0.2511"       -> "0.25-1-1"
 *
 * A trailing hyphen typed by the user is preserved while typing so the
 * cursor naturally continues into the next segment; normalizeFrequency()
 * strips it on commit/blur. A trailing INCOMPLETE decimal ("1." — dot typed,
 * no decimal digit yet, or "0.2" — one decimal digit, more expected) is
 * likewise preserved at end-of-string so the user can keep typing the
 * decimal digit(s) (e.g. on the way to "1.5" or "0.25"); it is stripped at
 * mid-string boundaries (hyphen, second dot) and on commit by
 * normalizeFrequency(). The function is idempotent on already-formatted
 * input: "1-1-1" stays "1-1-1".
 *
 * NOTE: whole-number doses are limited to a single digit (0-9) under this
 * rule. Two-digit whole doses like "10" are split into "1-0". Decimal doses
 * support up to TWO decimal places (e.g. "1.5", "0.5", "0.25", "0.75").
 * This matches the clinic's convention of single-digit whole doses
 * (1-1-1-1) and decimal doses including quarter/half/three-quarter
 * increments (0.25-0.5-0.75-1). The backend validator (FREQ_SEGMENT) also
 * accepts two-digit whole segments if entered via paste/other paths.
 */
export function autoFormatFrequency(raw) {
    // Strip anything that isn't a digit, dot, or hyphen.
    const s = raw.replace(/[^\d.-]/g, "");
    if (s === "")
        return "";
    // State machine: each dose segment is a single digit, optionally followed
    // by a dot and ONE or TWO decimal digits (e.g. "1", "1.5", "0.5", "0.25",
    // "0.75"). The next digit after a complete segment starts a new segment,
    // so hyphens are inserted automatically as the user types.
    //
    // States: EMPTY -> WHOLE ("d") -> DOT ("d.") -> DECIMAL1 ("d.x")
    //   -> DECIMAL2 ("d.xx")
    //   digit after WHOLE    -> new segment (flush, start fresh)
    //   digit after DECIMAL1  -> DECIMAL2 (complete the 2nd decimal place)
    //   digit after DECIMAL2 -> new segment (flush, start fresh)
    //   digit after DOT      -> DECIMAL1 (complete the 1st decimal)
    //   dot after WHOLE      -> DOT
    //   dot after DOT/DECIMAL -> flush current, drop the dot (second dot = boundary)
    //   hyphen               -> flush current segment
    const segments = [];
    let seg = "";
    let hasDot = false;
    let decimalCount = 0;
    let trailingHyphen = false;
    // flush() finalizes the current segment. `stripTrailingDot` controls what
    // happens to an INCOMPLETE decimal ("1." — dot typed, no decimal digit yet,
    // or "0.2" — one decimal digit, more expected):
    //   - mid-string boundary (hyphen, or a second dot): strip the trailing dot
    //     ("1.-" / "1..5" are malformed — the dot was a boundary, not a decimal).
    //   - end of string: PRESERVE the trailing dot so the user can continue
    //     typing the decimal digit(s) ("1." must stay "1." so the next keystroke
    //     "5" yields "1.5"; "0.2" must stay "0.2" so the next keystroke "5"
    //     yields "0.25"). Without this, the dot was stripped on every
    //     keystroke and decimals like 1.5 / 2.5 / 0.5 were impossible to type
    //     (postmortem 2026-09-22). A leftover trailing dot is cleaned up by
    //     normalizeFrequency() on blur/commit.
    const flush = (stripTrailingDot) => {
        if (seg !== "") {
            segments.push(stripTrailingDot && hasDot && decimalCount === 0 ? seg.replace(/\.$/, "") : seg);
        }
        seg = "";
        hasDot = false;
        decimalCount = 0;
    };
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === "-") {
            flush(true);
            if (i === s.length - 1)
                trailingHyphen = true;
            continue;
        }
        if (ch === ".") {
            if (seg === "" || hasDot) {
                // Leading dot, or second dot — drop it. If we had a decimal segment,
                // flush it so the next digit starts clean.
                if (hasDot)
                    flush(true);
                continue;
            }
            seg += ".";
            hasDot = true;
            continue;
        }
        // ch is a digit.
        if (hasDot && decimalCount === 0) {
            // DOT state -> complete the 1st decimal.
            seg += ch;
            decimalCount = 1;
        }
        else if (decimalCount === 1) {
            // DECIMAL1 state -> complete the 2nd decimal.
            seg += ch;
            decimalCount = 2;
        }
        else if (decimalCount === 2) {
            // DECIMAL2 state — digit after a complete d.xx segment starts a new one.
            flush(true);
            seg = ch;
        }
        else if (seg === "") {
            // EMPTY -> WHOLE.
            seg = ch;
        }
        else {
            // WHOLE state — digit after a whole number starts a new segment.
            flush(true);
            seg = ch;
        }
    }
    // Final flush: preserve a trailing incomplete decimal ("1.") so the user
    // can keep typing the decimal digit.
    flush(false);
    let result = segments.join("-");
    if (trailingHyphen && result !== "" && !result.endsWith("-")) {
        result += "-";
    }
    return result;
}
/**
 * Resolve the effective drug name for a taper step, falling back to the
 * row-level name when the step has no per-step override. Returns "" when
 * neither is set (shouldn't happen in practice — the row name is NOT NULL).
 */
export function stepDrugName(step, rowName) {
    return (step.name && step.name.trim()) ? step.name.trim() : rowName;
}
/**
 * Run-length group taper steps by their effective drug name, preserving the
 * entered order. Each group is a maximal run of consecutive steps with the
 * same resolved drug name. The print renderers use this to draw a per-group
 * header line ("t. Serlift 25mg") followed by the date-range lines for the
 * steps in that group. When every step shares the row-level name (legacy or
 * whole-protocol-one-strength mode), a single group is returned and the
 * output is identical to the old single-header behaviour.
 *
 * @param steps  the taper steps, in entered order
 * @param rowName the medicines row-level name (fallback for steps without
 *                a per-step `name`)
 */
export function groupTaperSteps(steps, rowName) {
    const groups = [];
    for (const step of steps) {
        const name = stepDrugName(step, rowName);
        const last = groups[groups.length - 1];
        if (last && last.name === name) {
            last.steps.push(step);
        }
        else {
            groups.push({ name, steps: [step] });
        }
    }
    return groups;
}
/**
 * Rebase a stored taper protocol onto a new start date. The first step starts
 * on `startDate` and every following step starts the day after the previous
 * step's To date (fromDate + days - 1) — the same chaining rule the protocol
 * builder's "+ append" uses. Only dates move: frequency, days, per-step
 * instructions, and per-step drug overrides are preserved verbatim.
 *
 * Use when INSERTING a stored protocol (prescription-template append,
 * copy-from-previous-visit Rx): a protocol's fromDate values are frozen at
 * entry/save time, so copying them verbatim would carry stale dates into a
 * prescription written on a later date. `startDate` must be a YYYY-MM-DD
 * string — callers derive it with `dateIST()` (never a local `Date` — see the
 * 2026-09-20 IST off-by-one postmortem; all arithmetic here is pure UTC).
 *
 * A malformed step (`days` < 1 or non-numeric) falls back to a 1-day span so
 * the chain stays sane rather than corrupting subsequent dates.
 */
export function rebaseTaper(steps, startDate) {
    let from = startDate;
    return steps.map((s) => {
        const step = { ...s, fromDate: from };
        const n = Number(s.days);
        from = addDaysIso(from, Number.isFinite(n) && n >= 1 ? n : 1);
        return step;
    });
}
/**
 * Pure-UTC date arithmetic on YYYY-MM-DD strings: returns the ISO date
 * `n` days after `iso`. Never uses local-`Date` parsing or `toISOString()`
 * on a local-parsed date (the 2026-09-20 IST off-by-one bug class) —
 * `Date.UTC` math is timezone-proof by construction.
 */
export function addDaysIso(iso, n) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
//# sourceMappingURL=drugs.js.map